import type { Book, Chapter } from "#/types";
import { BookStatus } from "#/types";

import { useAuth } from "#/feature/auth/auth-context";
import { useRequireAuth } from "#/feature/auth/use-require-auth";
import { privacySignal } from "#/feature/tts-playback/tts-store";
import { useSignal } from "#/use-signal";
import { getCoverUrl, getEpubDownloadUrl } from "#/client";
import {
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Loader2,
  RefreshCw,
  SortAsc,
  SortDesc,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useBookDetail } from "./use-book";
import {
  compactHeroClass,
  coverWrapperClass,
  coverGradientClass,
  coverExpandBtnClass,
  heroCoverClass,
  combinedInfoClass,
  heroHeaderClass,
  coverOverlayClass,
  coverOverlayImgClass,
  bookTitleClass,
  bookAuthorClass,
  badgeGroupClass,
  tabContainerClass,
  tabButton,
  descriptionTabContentClass,
  descriptionContentClass,
  tagsContainerClass,
  tagClass,
  genreVariantClass,
  tagVariantClass,
  resumeButtonContainerClass,
  resumeButtonMainClass,
  resumeButtonTextClass,
  resumeButtonChapterClass,
  resumeButtonDropdownClass,
  statItemClass,
  statLabelClass,
  statValueCompactClass,
  actionMenuContentClass,
  dialogOverlayClass,
  dialogBoxClass,
  dialogHeaderClass,
  dialogTitleClass,
  dialogBodyClass,
  dialogLabelClass,
  methodContainerClass,
  methodToggleClass,
  methodButton,
  chapterListContainerClass,
  chapterListHeaderClass,
  sortBtnClass,
  chapterListScrollClass,
  chapterGridClass,
  chapterItemClass,
  chapterTitleClass,
  emptyChaptersClass,
  listFooterClass,
  sourceBadgeClass,
  badgeBaseClass,
  badgeStatusCompletedClass,
  badgeStatusOngoingClass,
  badgeStatusHiatusClass,
  badgeStatusDroppedClass,
  badgeStatusUnknownClass,
  badgeDownloadFullClass,
  badgeDownloadPartialClass,
  badgeDownloadUnknownClass,
  downloadBadgeBtnClass,
  spinClass,
} from "./book-page.css";
import { Button } from "#/components/button/button";
import { Input } from "#/components/input/input";
import { cx } from "styled-system/css";
import { containerStyles, pageContainerStyles } from "#/app.css";
export function BookPage() {
  const allowed = useRequireAuth();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const privacyMode = useSignal(privacySignal);

  const {
    book,
    chapters,
    nextCursor,
    loading,
    fetchingChapters,
    checkingUpdates,
    sortOrder,
    useExtension,
    downloadLimit,
    setUseExtension,
    setDownloadLimit,
    checkUpdates,
    startDownload,
    removeBook,
    toggleSortOrder,
    fetchChapters,
  } = useBookDetail(id);

  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isCoverPreviewOpen, setIsCoverPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"description" | "chapters">("description");
  const [isResumeMenuOpen, setIsResumeMenuOpen] = useState(false);
  const resumeMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resumeMenuRef.current && !resumeMenuRef.current.contains(event.target as Node)) {
        setIsResumeMenuOpen(false);
      }
    };

    if (isResumeMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isResumeMenuOpen]);

  useEffect(() => {
    if (!isCoverPreviewOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsCoverPreviewOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isCoverPreviewOpen]);

  const handleDownloadEpub = (startChapterId: number, count: number) => {
    if (!id) {
      return;
    }
    globalThis.location.href = getEpubDownloadUrl(parseInt(id), startChapterId, count);
  };

  if (!allowed) return null;

  if (loading) {
    return <div className={emptyChaptersClass}>Loading book details...</div>;
  }

  if (!book) {
    return (
      <div className={emptyChaptersClass} style={{ color: "error" }}>
        Book not found.
      </div>
    );
  }

  return (
    <div className={pageContainerStyles} data-debug-id="book-details">
      <div className={compactHeroClass}>
        <div className={coverWrapperClass}>
          {!privacyMode ? (
            <img
              src={getCoverUrl(book.id)}
              alt={book.title}
              className={heroCoverClass}
              onError={(event) => {
                event.currentTarget.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' /%3E%3C/svg%3E";
              }}
            />
          ) : (
            <div
              className={heroCoverClass}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--colors-bg-base300)",
              }}
            >
              <div style={{ color: "var(--colors-text-base)", opacity: 0.2 }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  width="64"
                  height="64"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
            </div>
          )}

          <div className={coverGradientClass} />

          <button
            type="button"
            className={coverExpandBtnClass}
            onClick={() => setIsCoverPreviewOpen(true)}
            aria-label="View full cover"
          />
        </div>

        <div className={combinedInfoClass}>
          <div className={containerStyles}>
            <div className={heroHeaderClass}>
              <div style={{ textAlign: "left" }}>
                <h1 className={bookTitleClass}>{book.title}</h1>
                <p className={bookAuthorClass}>{book.authors.join(", ")}</p>
              </div>

              <div className={badgeGroupClass}>
                <span className={sourceBadgeClass}>{book.source_id}</span>
                <span
                  className={cx(
                    badgeBaseClass,
                    book.status === BookStatus.Completed && badgeStatusCompletedClass,
                    book.status === BookStatus.Ongoing && badgeStatusOngoingClass,
                    book.status === BookStatus.Hiatus && badgeStatusHiatusClass,
                    book.status === BookStatus.Dropped && badgeStatusDroppedClass,
                    (!book.status || book.status === BookStatus.Unknown) && badgeStatusUnknownClass,
                  )}
                >
                  {book.status || "Unknown"}
                </span>
                <span
                  className={cx(
                    badgeBaseClass,
                    book.download_status === "Full" && badgeDownloadFullClass,
                    book.download_status === "Partial" && badgeDownloadPartialClass,
                    book.download_status === "Unknown" && badgeDownloadUnknownClass,
                  )}
                >
                  {book.download_status}
                </span>
                {book.is_downloading && (
                  <Link
                    to={`/jobs?book_id=${book.id}`}
                    className={downloadBadgeBtnClass}
                    title="Downloading"
                  >
                    <Loader2 size={10} className={spinClass} />
                    Downloading
                  </Link>
                )}
              </div>

              {book.latest_chapter_title && (
                <div className={statItemClass}>
                  <span className={statLabelClass}>Latest Online</span>
                  <span className={statValueCompactClass}>
                    {book.latest_chapter_title.replace(/Chapter\s+/i, "")}
                  </span>
                </div>
              )}

              <ResumeButton
                book={book}
                isAdmin={isAdmin}
                isMenuOpen={isResumeMenuOpen}
                setIsMenuOpen={setIsResumeMenuOpen}
                menuRef={resumeMenuRef}
                checkUpdates={checkUpdates}
                checkingUpdates={checkingUpdates}
                setIsDownloadDialogOpen={setIsDownloadDialogOpen}
                removeBook={removeBook}
              />
            </div>

            {/* Tab Buttons */}
            <div className={tabContainerClass}>
              <Button
                variant="ghost"
                onClick={() => setActiveTab("description")}
                className={tabButton({ active: activeTab === "description" })}
              >
                Description
              </Button>
              <Button
                variant="ghost"
                onClick={() => setActiveTab("chapters")}
                className={tabButton({ active: activeTab === "chapters" })}
              >
                Chapters ({chapters.length})
              </Button>
            </div>

            {/* Tab Content */}
            {activeTab === "description" && (
              <div className={descriptionTabContentClass}>
                <GenresAndTags book={book} />
                {book.description && (
                  <div className={descriptionContentClass}>
                    {book.description.map((paragraph, index) => (
                      <p key={index} style={{ marginBottom: "0.5rem" }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "chapters" && (
              <ChapterList
                bookId={id}
                chapters={chapters}
                nextCursor={nextCursor}
                sortOrder={sortOrder}
                onToggleSort={toggleSortOrder}
                onFetchMore={() => fetchChapters(nextCursor)}
                isFetching={fetchingChapters}
              />
            )}
          </div>
        </div>
      </div>

      {isDownloadDialogOpen && (
        <DownloadDialog
          isOpen={isDownloadDialogOpen}
          onClose={() => setIsDownloadDialogOpen(false)}
          useExtension={useExtension}
          setUseExtension={setUseExtension}
          needsExtension={book.needs_extension}
          downloadLimit={downloadLimit}
          setDownloadLimit={setDownloadLimit}
          onStart={() => {
            void startDownload();
            setIsDownloadDialogOpen(false);
          }}
        />
      )}

      {isCoverPreviewOpen && (
        <div className={coverOverlayClass} onClick={() => setIsCoverPreviewOpen(false)}>
          <img src={getCoverUrl(book.id)} alt={book.title} className={coverOverlayImgClass} />
        </div>
      )}
    </div>
  );
}

const GenresAndTags = ({ book }: { book: Book }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const genres = book.genres || [];
  const tags = book.tags || [];

  const allItems = [
    ...genres.map((g) => ({ type: "genre" as const, value: g })),
    ...tags.map((t) => ({ type: "tag" as const, value: t })),
  ];

  if (allItems.length === 0) {
    return;
  }

  const shouldCollapse = allItems.length > 10;
  const visibleItems = shouldCollapse && !isExpanded ? allItems.slice(0, 10) : allItems;

  return (
    <div className={tagsContainerClass}>
      {visibleItems.map((item, index) => (
        <span
          key={`${item.type}-${item.value}-${index}`}
          className={cx(tagClass, item.type === "genre" ? genreVariantClass : tagVariantClass)}
        >
          {item.value}
        </span>
      ))}
      {shouldCollapse && (
        <Button
          variant="ghost"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ padding: "4px", opacity: 0.5 }}
        >
          {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </Button>
      )}
    </div>
  );
};

interface DownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  useExtension: boolean;
  setUseExtension: (value: boolean) => void;
  needsExtension: boolean;
  downloadLimit: number | undefined;
  setDownloadLimit: (value: number | undefined) => void;
  onStart: () => void;
}

const DownloadDialog = ({
  isOpen,
  onClose,
  useExtension,
  setUseExtension,
  needsExtension,
  downloadLimit,
  setDownloadLimit,
  onStart,
}: DownloadDialogProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className={dialogOverlayClass}>
      <div className={dialogBoxClass}>
        <div className={dialogHeaderClass}>
          <h3 className={dialogTitleClass}>Download Options</h3>
          <Button variant="ghost" size="small" onClick={onClose} style={{ padding: "8px" }}>
            <X size={20} />
          </Button>
        </div>
        <div className={dialogBodyClass}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div className={methodContainerClass}>
              <span className={dialogLabelClass}>Method</span>
              <div className={methodToggleClass}>
                <button
                  onClick={() => setUseExtension(false)}
                  disabled={needsExtension}
                  className={methodButton({
                    active: !useExtension,
                    disabled: needsExtension,
                  })}
                >
                  Server
                </button>
                <button
                  onClick={() => setUseExtension(true)}
                  className={methodButton({ active: useExtension })}
                >
                  Extension
                </button>
              </div>
            </div>

            <div className={methodContainerClass}>
              <span className={dialogLabelClass}>Max Chapters</span>
              <Input
                type="number"
                placeholder="Unlimited"
                value={downloadLimit ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = parseInt(e.target.value);
                  setDownloadLimit(isNaN(val) ? undefined : val);
                }}
              />
            </div>

            <Button
              onClick={onStart}
              fullWidth
              size="medium"
              style={{ padding: "0.5rem", borderRadius: "0.5rem", fontWeight: "900" }}
            >
              Start Download
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ResumeButtonProps {
  book: Book;
  isAdmin: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
  checkUpdates: () => void;
  checkingUpdates: boolean;
  setIsDownloadDialogOpen: (value: boolean) => void;
  removeBook: () => void;
}

const ResumeButton = ({
  book,
  isAdmin,
  isMenuOpen,
  setIsMenuOpen,
  menuRef,
  checkUpdates,
  checkingUpdates,
  setIsDownloadDialogOpen,
  removeBook,
}: ResumeButtonProps) => {
  const privacyMode = useSignal(privacySignal);
  const hasResumeChapter = book.resume_chapter_id != null;
  const buttonText = hasResumeChapter ? "Resume" : "Start";

  const handleMainClick = () => {
    if (book.resume_chapter_id) {
      window.location.href = `/book/${book.id}/chapter/${book.resume_chapter_id}`;
    }
  };

  return (
    <div style={{ position: "relative" }} ref={menuRef}>
      <div className={resumeButtonContainerClass}>
        <button
          onClick={handleMainClick}
          disabled={!hasResumeChapter}
          className={resumeButtonMainClass}
        >
          <span className={resumeButtonTextClass}>{buttonText}</span>
          {book.resume_chapter_title && (
            <>
              <span style={{ fontSize: "12px", opacity: 0.6, fontWeight: "700" }}>|</span>
              <span className={resumeButtonChapterClass}>{book.resume_chapter_title}</span>
            </>
          )}
        </button>
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={resumeButtonDropdownClass}>
          <ChevronDown size={14} />
        </button>
      </div>
      {isMenuOpen && (
        <ul className={actionMenuContentClass}>
          {!privacyMode && (
            <li>
              <Button
                as="a"
                href={book.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="ghost"
                onClick={() => setIsMenuOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  justifyContent: "flex-start",
                  padding: "8px",
                  paddingRight: "24px",
                  fontWeight: 400,
                  width: "100%",
                }}
              >
                <ExternalLink size={16} />
                Open Source
              </Button>
            </li>
          )}
          {isAdmin && (
            <>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void checkUpdates();
                    setIsMenuOpen(false);
                  }}
                  disabled={checkingUpdates}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    justifyContent: "flex-start",
                    padding: "8px",
                    paddingRight: "24px",
                    fontWeight: 400,
                    width: "100%",
                  }}
                >
                  {checkingUpdates ? (
                    <Loader2 size={16} className={spinClass} />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  Refresh Book
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsDownloadDialogOpen(true);
                    setIsMenuOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    justifyContent: "flex-start",
                    padding: "8px",
                    paddingRight: "24px",
                    fontWeight: 400,
                    width: "100%",
                  }}
                >
                  <Download size={16} />
                  Download...
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void removeBook();
                    setIsMenuOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    justifyContent: "flex-start",
                    padding: "8px",
                    paddingRight: "24px",
                    color: "error",
                    fontWeight: 400,
                    width: "100%",
                  }}
                >
                  <Trash2 size={16} />
                  Delete Book
                </Button>
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  );
};

interface ChapterListProps {
  bookId: string | undefined;
  chapters: Chapter[];
  nextCursor: number | undefined;
  sortOrder: "asc" | "desc";
  onToggleSort: () => void;
  onFetchMore: () => void;
  isFetching: boolean;
}

const ChapterList = ({
  bookId,
  chapters,
  nextCursor,
  sortOrder,
  onToggleSort,
  onFetchMore,
  isFetching,
}: ChapterListProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        const lastChapter = chapters.length > 0 ? chapters[chapters.length - 1] : null;
        if (target && target.isIntersecting && nextCursor && !isFetching) {
          onFetchMore();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 0.1,
      },
    );
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [nextCursor, isFetching, onFetchMore, chapters]);

  return (
    <div className={chapterListContainerClass} data-debug-id="chapter-list">
      <div className={chapterListHeaderClass}>
        <Button variant="ghost" size="small" onClick={onToggleSort} className={sortBtnClass}>
          {sortOrder === "asc" ? <SortAsc size={12} /> : <SortDesc size={12} />}
          {sortOrder === "asc" ? "Oldest" : "Latest"}
        </Button>
      </div>

      <div className={chapterListScrollClass}>
        {chapters.length > 0 ? (
          <>
            <div className={chapterGridClass}>
              {chapters.map((chapter) => (
                <div key={chapter.id} className={chapterItemClass}>
                  <Link
                    to={`/book/${bookId}/chapter/${chapter.id}`}
                    className={chapterTitleClass}
                    title={chapter.title}
                    style={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}
                  >
                    {chapter.title}
                  </Link>
                </div>
              ))}
            </div>
            <div
              ref={loaderRef}
              className={listFooterClass}
              style={{ minHeight: "20px", padding: "10px" }}
            >
              {isFetching
                ? "Loading more... "
                : nextCursor
                  ? "Load more"
                  : `End of list (${chapters.length} chapters)`}
            </div>
          </>
        ) : (
          <div className={emptyChaptersClass}>
            {isFetching ? "Loading chapters..." : "No chapters downloaded"}
          </div>
        )}
      </div>
    </div>
  );
};
